import { ShopifyStore, getBaseStore } from 'lib/commerce/shopify/shopifyHelper';
import type { Cart } from 'lib/commerce/cart/types';
import { COUNTRY_STORE_CONFIG } from 'lib/region/countryStoreConfig';
import {
  buildRegionLocaleSlug,
  getDefaultLanguageForRegion,
  getRegionConfig,
  getSupportedLanguagesForRegion,
  parseRegionLocale,
  parseRegionPreference,
} from 'lib/region/regionLocale';

// Gift card redeem: the code never goes in a URL. /api/gift-card/redeem puts it
// in an HttpOnly cookie only the apply route receives; the cart provider calls that route.

/** HttpOnly, scoped to GIFT_CARD_APPLY_PATH, so no page request carries it. */
export const GIFT_CARD_CODE_COOKIE = 'uh_gift_card_code';
/** Readable flag, never the code: the website store to apply on, or 'error'. */
export const GIFT_CARD_PENDING_COOKIE = 'uh_gift_card_pending';
export const GIFT_CARD_PENDING_ERROR = 'error';
export const GIFT_CARD_APPLY_PATH = '/api/vendor/shopify/cart/giftCards';

/** Cookie options for both cookies. maxAge 0 clears; the path must match to clear. */
export const giftCardCookieOptions = (maxAge: number) => {
  const base = { secure: true, sameSite: 'lax' as const, maxAge };
  return {
    code: { ...base, httpOnly: true, path: GIFT_CARD_APPLY_PATH },
    pending: { ...base, path: '/' },
  };
};
export const GIFT_CARD_COOKIE_MAX_AGE = 600;

/** Shopify codes are 8-20 letters and digits; emails show them in spaced groups. */
export function normalizeGiftCardCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.replace(/[\s-]/g, '').toUpperCase();
  return /^[A-Z0-9]{8,20}$/.test(code) ? code : null;
}

/**
 * Shopify gift card page `store` → the website's Shopify base store, and the
 * only origins that page posts from: its primary domain and its myshopify host
 * (each store's /meta.json). Any other Shopify merchant, ultrahuman.com
 * subdomain or store could otherwise plant a code.
 */
const GIFT_CARD_PAGES: Record<
  string,
  { base: ShopifyStore; origins: readonly string[] }
> = {
  us: {
    base: ShopifyStore.US,
    origins: [
      'https://checkout.us.ultrahuman.com',
      'https://stelcore-x-ultrahuman.myshopify.com',
    ],
  },
  row: {
    base: ShopifyStore.ROW,
    origins: [
      'https://checkout.global.ultrahuman.com',
      'https://ultrahuman-us.myshopify.com',
    ],
  },
  eu_uk: {
    base: ShopifyStore.EU,
    origins: [
      'https://checkout.euruk.ultrahuman.com',
      'https://ultrahuman-eu-uk.myshopify.com',
    ],
  },
  uae: {
    base: ShopifyStore.AE,
    origins: [
      'https://checkout.uae.ultrahuman.com',
      'https://ultrahuman-mena.myshopify.com',
    ],
  },
  india: {
    base: ShopifyStore.IN,
    origins: [
      'https://checkout.in.ultrahuman.com',
      'https://ultrahumanstore.myshopify.com',
    ],
  },
};

const giftCardPage = (store: unknown) => {
  const key = String(store ?? '').toLowerCase();
  return Object.hasOwn(GIFT_CARD_PAGES, key) ? GIFT_CARD_PAGES[key] : null;
};

/** Only the posted store's own gift card page may post a code: exact origin, no port. A missing or `null` Origin is refused. */
export const isRedeemOrigin = (origin: unknown, store: unknown): boolean =>
  typeof origin === 'string' && !!giftCardPage(store)?.origins.includes(origin);

// Every website region; ROW is the /global/ slug.
const REGIONS = Object.keys(COUNTRY_STORE_CONFIG).map((cc) =>
  cc === 'ROW' ? 'GLOBAL' : cc,
);

// A store's default region is the one named like it (US, CA, GB, AU, JP, MX,
// SA, ZA, AE, IN); EU and ROW have no such region.
const STORE_DEFAULT_REGION: Partial<Record<ShopifyStore, string>> = {
  [ShopifyStore.EU]: 'DE',
  [ShopifyStore.ROW]: 'GLOBAL',
};
const defaultRegionOf = (store: ShopifyStore) =>
  STORE_DEFAULT_REGION[store] ?? store;

// Where a region's redeem lands, after its slug. Default: the home page.
const REGION_LANDING_PATH: Partial<Record<string, string>> = {
  US: 'ring-pro/buy/',
};

export type RedeemVisitor = {
  /** `uh_region_preference`: an explicit choice in the region selector. */
  regionCookie?: string | null;
  /** Geo country header, as proxy.ts reads it. */
  country?: string | null;
  acceptLanguage?: string | null;
};

/**
 * Home page for a gift card from the gift card page's `store` + `currency`,
 * from the website's own region config. A card spends on every region of its
 * Shopify store (Shopify converts it into the checkout's currency). The region
 * cookie wins, then the geo country, then the region selling in the card's
 * currency, then the store's default. Language: the cookie's, then
 * Accept-Language, then the region's default. It lands on the region's home
 * page (US: the Ring PRO buy page). `store` is the website store to apply on.
 */
export function redeemLanding(
  store: unknown,
  currency: unknown,
  visitor: RedeemVisitor = {},
): { store: ShopifyStore; path: string } | null {
  const base = giftCardPage(store)?.base;
  if (!base) return null;
  const cur = String(currency ?? '').toLowerCase();
  const candidates = REGIONS.filter((code) => {
    const r = getRegionConfig(code);
    return (r.baseStore ?? r.shopifyStore) === base;
  });
  if (!candidates.length) return null;
  const inCurrency = candidates.find((c) => getRegionConfig(c).currency === cur);

  const pref = parseRegionPreference(visitor.regionCookie);
  const chosen = pref ? parseRegionLocale(pref) : null;
  const country = visitor.country?.toUpperCase();
  const fallback = defaultRegionOf(
    inCurrency ? getRegionConfig(inCurrency).shopifyStore : base,
  );
  const region =
    [chosen?.region, country, fallback].find(
      (r): r is string => !!r && candidates.includes(r),
    ) ?? candidates[0];

  const languages = getSupportedLanguagesForRegion(region);
  const language =
    [chosen?.language, ...acceptedLanguages(visitor.acceptLanguage)].find(
      (l): l is string => !!l && languages.includes(l),
    ) ?? getDefaultLanguageForRegion(region);

  return {
    store: getRegionConfig(region).shopifyStore,
    path: `/${buildRegionLocaleSlug(region, language)}/${REGION_LANDING_PATH[region] ?? ''}`,
  };
}

/** Accept-Language, best first, spelled as the site spells languages ('fr', 'zh-tw'). */
function acceptedLanguages(header: string | null | undefined): string[] {
  return (header ?? '')
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().toLowerCase().split(';');
      const q = params.find((p) => p.trim().startsWith('q='));
      return {
        lang: /^zh-(tw|hant|hk|mo)/.test(tag) ? 'zh-tw' : tag.split('-')[0],
        q: q ? Number(q.trim().slice(2)) : 1,
      };
    })
    .filter((x) => x.lang && x.q > 0)
    .sort((a, b) => b.q - a.q)
    .map((x) => x.lang);
}

/** A card pending for one store applies on any store sharing its Shopify backend (StoreAliasGroups). */
export function sameGiftCardStore(a: string, b: string | null | undefined): boolean {
  const stores = Object.values(ShopifyStore) as string[];
  return (
    !!b &&
    stores.includes(a) &&
    stores.includes(b) &&
    getBaseStore(a as ShopifyStore) === getBaseStore(b as ShopifyStore)
  );
}

/** The card the redeem added: on the cart after, not before. Shopify drops a bad code silently. */
export function newGiftCard<T extends { id: string }>(
  beforeIds: readonly string[],
  after: T[] | null | undefined,
): T | undefined {
  return after?.find((c) => !!c.id && !beforeIds.includes(c.id));
}

/**
 * The apply route's answer. Shopify couldn't be reached (network, throttle,
 * 5xx): 502 with `retry`, and both cookies stay so the next page load tries
 * again. Otherwise Shopify answered (applied, or refused the code): 200, and
 * both cookies are cleared.
 */
export function giftCardApplyResult({
  failed,
  cart,
  before,
  cartId,
}: {
  failed: boolean;
  cart: Cart | null;
  before: readonly string[];
  cartId: string | null;
}) {
  if (failed) {
    return {
      status: 502,
      clearCookies: false,
      body: { applied: false, retry: true, lastCharacters: null, cart: null },
    };
  }
  const card = newGiftCard(before, cart?.appliedGiftCards);
  return {
    status: 200,
    clearCookies: true,
    body: {
      applied: !!card,
      retry: false,
      lastCharacters: card?.lastCharacters ?? null,
      // A cart made only to try a refused code is not handed back.
      cart: card || cartId ? cart : null,
    },
  };
}
